import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { aiSelector, getAiPromptResponse } from '../../store/feature/aiSlice';
import { get_dynamic_styles } from '../../constant/DynamicStyles';
import { listingSelector } from '../../store/feature/listingSlice';
import { accountSelector } from '../../store/feature/accountSlice';

const LoadingState = ({ styles }) => (
  <div
    className="w-full flex items-center justify-center"
    style={get_dynamic_styles(styles?.main_styles)}
  >
    <div
      className="w-full max-w-4xl animate-pulse"
      style={get_dynamic_styles(styles?.box_styles)}
    >
      {/* Title and message skeletons */}
      <div className="space-y-3 mb-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>

      {/* Table skeleton */}
      <div className="overflow-auto mt-4">
        <div className="min-w-full border border-gray-200 rounded-lg">
          {/* Header skeleton */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-4 gap-4 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-4 bg-gray-200 rounded"
                  style={{ width: `${Math.random() * 40 + 60}%` }} // Random widths
                ></div>
              ))}
            </div>
          </div>

          {/* Rows skeleton */}
          {[1, 2, 3, 4, 5].map((row) => (
            <div
              key={row}
              className="grid grid-cols-4 gap-4 p-4 border-b border-gray-100 last:border-0"
            >
              {[1, 2, 3, 4].map((cell) => (
                <div
                  key={cell}
                  className="h-4 bg-gray-100 rounded"
                  style={{ width: `${Math.random() * 40 + 60}%` }} // Random widths
                ></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const AIPromptResponse = ({ styles, data }) => {
  const dispatch = useDispatch();
  const { responses, isFetching } = useSelector(aiSelector);
  const { listing_details } = useSelector(listingSelector);
  const { account_details } = useSelector(accountSelector);

  // prompt merge tags if {listing_id} is present than will take listing id
  const prompt = useMemo(() => {
    return data?.prompt.replace(/\{(\w+)\}/g, (match, p1) => {
      if (p1 === 'listing_id') {
        return listing_details?.id;
      }
      if (p1 === 'account_id') {
        return account_details?.id;
      }
      if (p1 === 'account_name') {
        return account_details?.name;
      }
      if (p1 === 'listing_title') {
        return listing_details?.title;
      }

      return responses[data?.prompt]?.[p1];
    });
  }, [data?.prompt, listing_details?.id, responses, account_details?.id]);

  useEffect(() => {
    if (prompt) {
      dispatch(getAiPromptResponse({ prompt }));
    }
  }, [prompt, dispatch]);

  // Get response for this specific prompt
  const promptResponse = responses[prompt];
  const isLoading = isFetching[prompt];

  const results = useMemo(() => {
    return (
      promptResponse?.reports ??
      promptResponse?.listings ??
      promptResponse?.data ??
      []
    );
  }, [promptResponse]);

  if (isLoading) {
    return <LoadingState styles={styles} />;
  }

  return (
    <div
      className="w-full flex items-center justify-center"
      style={get_dynamic_styles(styles?.main_styles)}
    >
      {prompt && promptResponse && (
        <div
          className="w-full max-w-4xl flex flex-col"
          style={get_dynamic_styles(styles?.box_styles)}
        >
          <h3 className="text-2xl font-bold">{promptResponse?.title}</h3>
          <p className="text-sm">{promptResponse?.success_message}</p>

          {/* Dynamic Report Table */}
          {results.length > 0 && (
            <div className="overflow-auto mt-4">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {results.length > 0 &&
                      Object.keys(results[0]).map((key) => (
                        <th
                          key={key}
                          className="text-left py-2 px-2 text-xs font-medium text-gray-500 whitespace-nowrap"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {results.slice(0, 5).map((row, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}
                    >
                      {Object.values(row).map((value, idx) => (
                        <td
                          key={idx}
                          className="py-3 px-2 text-sm text-gray-700 whitespace-nowrap"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIPromptResponse;
